import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  CreditCardIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ComputerDesktopIcon,
  WifiIcon,
  CloudIcon,
  BoltIcon,
  NoSymbolIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const TERMS_QUICK_SEARCHES = [
  'equipment',
  'pickup',
  '$100',
  'payment',
  'cancellation',
  'reconnection',
  'router',
  'E911',
  'SMS'
];

const Terms: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'wireless' | 'voip'>('wireless');
  const [searchQuery, setSearchQuery] = useState('');
  const [matchCount, setMatchCount] = useState<number | null>(null);

  const normalizedQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);

  useEffect(() => {
    const blocks = Array.from(document.querySelectorAll<HTMLElement>('[data-terms-block="true"]'));
    if (!normalizedQuery) {
      blocks.forEach((el) => el.classList.remove('hidden'));
      setMatchCount(null);
      return;
    }

    let visible = 0;
    blocks.forEach((el) => {
      const text = (el.textContent || '').toLowerCase();
      const hit = text.includes(normalizedQuery);
      el.classList.toggle('hidden', !hit);
      if (hit) visible += 1;
    });
    setMatchCount(visible);

    // Scroll to first match for quicker finding
    const first = blocks.find((el) => !el.classList.contains('hidden'));
    if (first) {
      first.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [normalizedQuery, activeTab]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-airmax-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Terms and Conditions
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Please read these terms carefully before using Airmax Internet services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('wireless')}
              className={`py-4 px-6 font-semibold text-lg border-b-4 transition-colors ${
                activeTab === 'wireless'
                  ? 'border-airmax-blue text-airmax-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center">
                <WifiIcon className="h-5 w-5 mr-2" />
                Wireless Internet
              </div>
            </button>
            <button
              onClick={() => setActiveTab('voip')}
              className={`py-4 px-6 font-semibold text-lg border-b-4 transition-colors ${
                activeTab === 'voip'
                  ? 'border-airmax-blue text-airmax-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 mr-2" />
                VoIP Phone Service
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <label htmlFor="terms-search" className="sr-only">Search terms</label>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              id="terms-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search terms (equipment, payment, cancellation, pickup…)"
              className="w-full rounded-xl border border-gray-300 bg-white py-3.5 pl-12 pr-12 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-airmax-blue focus:border-airmax-blue"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {TERMS_QUICK_SEARCHES.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => setSearchQuery(term)}
                className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                  normalizedQuery === term.toLowerCase()
                    ? 'border-airmax-blue bg-blue-50 text-airmax-blue'
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-airmax-blue hover:text-airmax-blue'
                }`}
              >
                {term}
              </button>
            ))}
          </div>
          {normalizedQuery && (
            <p className="mt-3 text-sm text-gray-600">
              {matchCount === 0
                ? `No sections matched “${searchQuery.trim()}”. Try another word or clear the search.`
                : `${matchCount} section${matchCount === 1 ? '' : 's'} matched “${searchQuery.trim()}”.`}
            </p>
          )}
        </div>
      </section>

      {/* Wireless Internet Content */}
      {activeTab === 'wireless' && (
        <>
      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div data-terms-block="true" className="bg-blue-50 rounded-xl p-8 border-2 border-blue-200 terms-block">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">ACCEPTANCE OF TERMS AND CONDITIONS</h2>
              <p className="text-gray-700 mb-4">
                By creating an AirMax Internet L.L.C. account, submitting a service request, scheduling installation, activating service, or using AirMax Internet L.L.C. services, the Client acknowledges that they have been presented with, reviewed, and agreed to these Terms and Conditions.
              </p>
              <p className="text-gray-700 mb-4">
                The Client understands and agrees that acceptance of these Terms occurs through, but is not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Account creation on the AirMax website</li>
                <li>Acceptance during service signup or checkout</li>
                <li>Account activation</li>
                <li>Payment of invoices that reference these Terms and Conditions</li>
                <li>Continued use of AirMax Internet L.L.C. services</li>
              </ul>
              <p className="text-gray-700 mt-4">
                These Terms and Conditions are made available to the Client at the time of signup, during account activation, and through ongoing account communications, including invoices.
              </p>
              <div className="bg-yellow-50 rounded-lg p-4 mt-4 border border-yellow-200">
                <p className="text-gray-700 font-medium">
                  <strong>Important:</strong> Continued use of the service constitutes ongoing and binding acceptance of these Terms and any associated policies, including the Acceptable Use Policy. If the Client does not agree to these Terms, the Client must not activate service and must immediately discontinue use of the service.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Service Provided */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <WifiIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">SERVICE PROVIDED</h2>
            </div>
            
            <p className="text-gray-700">
              AirMax Internet L.L.C. ("Provider") agrees to furnish the Client with the selected package of Wireless Internet Access Service as agreed upon at the time of installation. The Provider will deliver the service with reasonable care and skill in accordance with industry standards.
            </p>
          </motion.div>

          {/* Term of Agreement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">TERM OF AGREEMENT</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              This Agreement remains in effect from the start of service and continues as long as the service is provided. The Client may cancel the service at any time, but must notify AirMax Internet L.L.C. at least 10 days in advance of the intended cancellation date.
            </p>

            <div className="bg-orange-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Requirements:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Advance Notice:</strong> 10 days notice required for cancellation</li>
                <li><strong>Equipment Ownership:</strong> AirMax Internet L.L.C. retains ownership of all equipment, antennas, cables, and select software</li>
                <li><strong>Equipment Return / Pickup:</strong> Upon termination of service, all company-owned equipment must be returned to AirMax Internet L.L.C. or made available for AirMax pickup. Pickup scheduling is based on AirMax&apos;s calendar and may take up to <strong>90 days</strong></li>
                <li><strong>Non-Return Charge:</strong> If equipment is not returned to AirMax (or cannot be picked up), a charge of <strong>$100.00 per unit</strong> will be added to the Client&apos;s account</li>
              </ul>
            </div>
          </motion.div>

          {/* Payment Schedule */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <CreditCardIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">PAYMENT SCHEDULE</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              The Client may choose to be billed monthly for the selected Wireless Internet Access Service. AirMax Internet L.L.C. reserves the right to request payment for all equipment associated with the initial installation in advance.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Payment Due Date</h4>
                <p className="text-gray-700 text-sm">Service charges are due on the service commencement date</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Late Payment</h4>
                <p className="text-gray-700 text-sm">Failure to pay by due date may result in temporary disconnection</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">AutoPay Information</h3>
              <p className="text-gray-700 mb-4">If the Client activates autopay on their account:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>System will automatically process due invoices on scheduled payment date</li>
                <li>Client is responsible for maintaining accurate payment information</li>
                <li>Client must ensure sufficient funds are available</li>
                <li>AirMax Internet L.L.C. is not liable for service disruptions from failed payments</li>
                <li>By enabling autopay, Client agrees to allow processing of due or past-due invoices without further notice</li>
              </ul>
            </div>
          </motion.div>

          {/* Overdue Payments */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">POLICY FOR OVERDUE PAYMENTS AND RECONNECTION</h2>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                If a client's invoice remains unpaid for more than 10 days past the due date:
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-700">
                <li>Airmax will arrange for the pickup of antennas and routers</li>
                <li>After equipment pickup, a <strong>$25.00 reconnection fee</strong> plus all outstanding invoices must be paid in full</li>
                <li>Failure to settle fees and balance will result in <strong>permanent disconnection</strong></li>
              </ol>
            </div>
          </motion.div>

          {/* Changes to Service */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ArrowPathIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">CHANGES TO SERVICE</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Package Changes</h4>
                <p className="text-gray-700 text-sm">
                  Client may request to change their Wireless Internet Access package or speed without violating the terms. <strong>No fee</strong> for service package changes.
                </p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Service Relocation</h4>
                <p className="text-gray-700 text-sm">
                  Relocating service to a new address will incur a <strong>minimum fee of $100.00</strong>.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6 mt-6">
              <p className="text-gray-700">
                <strong>No early termination fees apply</strong>, but all outstanding balances must be settled prior to service termination or transfer.
              </p>
            </div>
          </motion.div>

          {/* Cancellation Policy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <NoSymbolIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">CANCELLATION & PREPAID SERVICE POLICY</h2>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Policy Points:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Prepaid Basis:</strong> Services are provided on a prepaid basis</li>
                <li><strong>Non-Refundable:</strong> Monthly service charges are non-refundable once processed</li>
                <li><strong>10-Day Notice:</strong> Must request cancellation at least 10 days prior to billing date</li>
                <li><strong>No Prorated Refunds:</strong> No refunds for unused days, partial months, or early termination</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">AutoPay Responsibility</h3>
              <p className="text-gray-700">
                Customers enrolled in AutoPay are responsible for ensuring cancellation requests are submitted within the required notice period. Failure to cancel in time does not constitute grounds for a refund.
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Exceptions:</strong> Any refunds or credits, if considered, are issued solely at the discretion of AirMax Internet L.L.C.
              </p>
            </div>
          </motion.div>

          {/* Client Installation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <WrenchScrewdriverIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">CLIENT INSTALLATION</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              The installation date and time will be determined by AirMax Internet L.L.C. and communicated to the Client as early as possible. Before or during installation, AirMax Internet L.L.C. will verify whether the Client's computer(s) are appropriately configured for the Wireless Internet Access Service.
            </p>

            <div className="bg-orange-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Responsibilities:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>If additional hardware is needed, Client must purchase or provide necessary equipment</li>
                <li>AirMax Internet L.L.C. is not responsible for issues from Client's internal network setup</li>
                <li>Service calls related to computer or network problems will incur a fee</li>
              </ul>
            </div>
          </motion.div>

          {/* Equipment Ownership */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ComputerDesktopIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">EQUIPMENT OWNERSHIP AND REMOVAL POLICY</h2>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                <strong>AirMax Internet L.L.C. retains full ownership</strong> of all equipment provided to clients, including but not limited to antennas, routers, and other telecommunications hardware.
              </p>
              <p className="text-gray-700">
                <strong>Return / Pickup requirement:</strong> When service ends, equipment must be returned to AirMax Internet L.L.C. or scheduled for AirMax pickup. Pickup timing depends on AirMax&apos;s calendar and may take up to <strong>90 days</strong>. Equipment that is not returned (or not made available for pickup) will result in a <strong>$100.00 charge per unit</strong> added to the Client&apos;s account.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  title: "Authorized Access",
                  description: "AirMax personnel or authorized contractors are permitted to access, repair, or replace company-owned equipment at any time as needed to maintain service quality."
                },
                {
                  title: "Service Cancellation",
                  description: "Upon cancellation or termination, AirMax is not required to seek further permission to remove its equipment and retains the right to retrieve any installed equipment. Clients must return equipment to AirMax or arrange pickup. Scheduled pickups are based on AirMax's calendar and may take up to 90 days."
                },
                {
                  title: "Client Responsibility for Access",
                  description: "Clients are responsible for providing necessary access to premises for equipment retrieval. Inaccessible properties may result in rescheduling at client's expense. Pickup windows may take up to 90 days depending on AirMax's calendar."
                },
                {
                  title: "Non-Return / Unrecoverable Equipment",
                  description: "If company-owned equipment is not returned to AirMax Internet L.L.C. and cannot be picked up, a charge of $100.00 per unit will be added to the Client's account. Unauthorized use, relocation, or tampering after cancellation may result in additional fees or legal action."
                }
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-airmax-blue">
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-700 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Storm Damages */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <CloudIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">STORM DAMAGES</h2>
            </div>
            
            <div className="bg-yellow-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                AirMax Internet L.L.C. is dedicated to maintaining continuous service. However, in the event of electrical storms, service interruptions may occur due to damage to our infrastructure.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900">Restoration Goal</p>
                  <p className="text-sm text-gray-700">Within 24 hours, weather permitting</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900">Maximum Timeframe</p>
                  <p className="text-sm text-gray-700">Complete repairs within 48 hours</p>
                </div>
              </div>
              <p className="text-gray-700 mt-4 text-sm">
                <strong>Disclaimer:</strong> The Provider is not liable for service interruptions caused by severe weather conditions beyond its control.
              </p>
            </div>
          </motion.div>

          {/* Router Warranty */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <WifiIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">WIRELESS ROUTER WARRANTY</h2>
            </div>
            
            <div className="bg-green-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                AirMax Internet L.L.C. provides each client with a basic WiFi Wireless Router at no additional cost.
              </p>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900 mb-2">Warranty Period:</p>
                <p className="text-sm text-gray-700">30 days from installation date</p>
              </div>
            </div>

            <div className="bg-red-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Limitations:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>AirMax is not responsible for malfunctions due to manufacturing defects, electrostatic discharge (ESD), electrical storms, or other related issues</li>
                <li>If router is defective, Client must purchase a new router before service call can be scheduled</li>
                <li>Client is responsible for ensuring router functionality post-installation</li>
              </ul>
            </div>
          </motion.div>

          {/* Service Obstruction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">SERVICE OBSTRUCTION BY TREES</h2>
            </div>
            
            <div className="bg-orange-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                <strong>Client Responsibility:</strong> Clients are advised to maintain their property, including keeping trees pruned and removing any foliage or branches that may obstruct the wireless signal.
              </p>
              <p className="text-gray-700">
                AirMax Internet L.L.C. is not responsible for maintaining a clear signal path. It is the Client's responsibility to ensure that the installation site remains free from obstructions that could impact service quality.
              </p>
            </div>
          </motion.div>

          {/* Client-Provided Equipment */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ComputerDesktopIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">CLIENT-PROVIDED EQUIPMENT</h2>
            </div>
            
            <div className="bg-yellow-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                Any equipment not purchased from AirMax Internet L.L.C. is considered client-provided equipment.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>AirMax Internet L.L.C. is not responsible for supporting client-provided equipment</li>
                <li>Client will be liable for service call expenses if such equipment adversely affects service</li>
                <li>Client is responsible for ensuring their equipment is compatible with the service provided</li>
              </ul>
            </div>
          </motion.div>

          {/* Consequential Damages */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">CONSEQUENTIAL DAMAGES</h2>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6">
              <p className="text-gray-700">
                AirMax Internet L.L.C. is not liable for any incidental or consequential damages resulting from the failure or suspension of Wireless Internet Access services. This includes but is not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mt-4">
                <li>Loss of data</li>
                <li>Loss of revenue</li>
                <li>Any other damages resulting from service interruptions</li>
              </ul>
            </div>
          </motion.div>

          {/* Service Calls */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.3 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <WrenchScrewdriverIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">SERVICE CALLS</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">General Service Calls</h4>
                <p className="text-gray-700 text-sm">Generally free</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Cable Replacement</h4>
                <p className="text-gray-700 text-sm">$1.50 per foot (unless Client has AirMax Protection Insurance)</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Warranty Information</h3>
              <p className="text-gray-700 mb-4">
                The manufacturer warrants the provided Wireless Internet Access Point/Router for one (1) year. During this period:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>AirMax will support hardware for problems covered by manufacturer's warranty</li>
                <li>Service calls related to out-of-warranty routers will be charged to Client at prevailing rates</li>
              </ul>
            </div>
          </motion.div>

          {/* Service Delivery */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <BoltIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">SERVICE DELIVERY</h2>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                Wireless Internet Access connection speeds (50Mbps to 200+Mbps, depending on package chosen) are measured between the Client's location and the AirMax Internet L.L.C. access point.
              </p>
            </div>

            <div className="bg-yellow-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Speed Variations</h3>
              <p className="text-gray-700 mb-4">
                Speeds may vary due to high Internet usage, and actual data transmission rates may be lower due to factors such as:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Internet congestion</li>
                <li>Server or router speeds</li>
                <li>Protocol overheads</li>
                <li>Other conditions beyond AirMax Internet L.L.C.'s control</li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                <strong>Disclaimer:</strong> The Provider does not guarantee uninterrupted service or specific connection speeds.
              </p>
            </div>
          </motion.div>

          {/* Installation Delay */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.5 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">INSTALLATION DELAY</h2>
            </div>
            
            <div className="bg-orange-50 rounded-xl p-6">
              <p className="text-gray-700">
                AirMax Internet L.L.C. is not liable for any delays in the delivery or installation of Wireless Internet Access Service, nor for any damages suffered by the Client due to such delays. Delays may occur due to factors beyond the Provider's control, and AirMax Internet L.L.C. will make reasonable efforts to minimize such delays.
              </p>
            </div>
          </motion.div>

          {/* Excessive Use Policy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.6 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <BoltIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">EXCESSIVE USE POLICY</h2>
            </div>
            
            <div className="bg-yellow-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                AirMax Internet L.L.C. enforces an Excessive Use Policy to ensure fair usage of the network. While the vast majority of Clients use their connection responsibly, an extremely small percentage may engage in excessive use that negatively impacts other Clients.
              </p>
            </div>

            <div className="bg-blue-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                In such cases, AirMax Internet L.L.C. reserves the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Throttle the network speed of any Client who exceeds fair usage limits</li>
              </ul>
              <p className="text-gray-700 mt-4">
                <strong>Note:</strong> This policy is rarely enforced and is intended to maintain service quality for all Clients.
              </p>
            </div>
          </motion.div>

          {/* Commitment to Service */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.7 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">COMMITMENT TO SERVICE</h2>
            </div>
            
            <div className="bg-green-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                AirMax Internet L.L.C. is committed to being the premier Wireless Internet Access Service Provider in Arizona. Our Network Operations Center (NOC) Engineers proactively monitor network performance to ensure adequate bandwidth is available for all Clients.
              </p>
              <p className="text-gray-700">
                While unexpected traffic on our network or the broader Internet can impact throughput speeds, AirMax Internet L.L.C. is dedicated to addressing these issues promptly. We continually evaluate and adopt new technologies to improve our network and deliver state-of-the-art services to our Clients.
              </p>
            </div>
          </motion.div>

          {/* Termination for Prohibited Use */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.8 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                <NoSymbolIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">TERMINATION FOR PROHIBITED USE</h2>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                AirMax Internet L.L.C. reserves the absolute right to suspend or terminate a Client's service immediately and without prior notice if the Client is found to be engaging in any prohibited activities, including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Violation of the Acceptable Use Policy</li>
                <li>Illegal activities of any kind</li>
                <li>Copyright infringement, including peer-to-peer (P2P) file sharing such as BitTorrent</li>
                <li>Use of unauthorized or illegal IPTV services, streaming boxes, applications, or platforms</li>
                <li>Network abuse, excessive usage, or activities that negatively impact network performance or other clients</li>
              </ul>
            </div>

            <div className="space-y-4">
              <div className="bg-orange-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Termination Conditions</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>With or without warning</li>
                  <li>Without refund or credit of any prepaid service</li>
                  <li>At the sole discretion of AirMax Internet L.L.C.</li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Post-Termination Requirements</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Client remains responsible for all outstanding balances</li>
                  <li>AirMax may immediately disable service access</li>
                  <li>All company-owned equipment must be returned to AirMax Internet L.L.C. or made available for AirMax pickup upon demand. Pickup scheduling depends on AirMax&apos;s calendar and may take up to <strong>90 days</strong></li>
                  <li>Failure to return equipment (or make it available for pickup) will result in a <strong>$100.00 charge per unit</strong> added to the Client&apos;s account, and may result in additional fees or legal action</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-gray-700 text-sm">
                  <strong>Important:</strong> AirMax Internet L.L.C. is under no obligation to provide evidence, logs, or technical details related to enforcement actions taken under this policy.
                </p>
              </div>
            </div>
          </motion.div>

        </div>
      </section>
        </>
      )}

      {/* VoIP Phone Service Content */}
      {activeTab === 'voip' && (
        <>
      {/* VoIP E911 Policy */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* Purpose */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <PhoneIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">VOIP EMERGENCY SERVICES (E911) RISK MANAGEMENT POLICY</h2>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">1. PURPOSE</h3>
              <p className="text-gray-700 mb-4">
                This policy outlines the procedures and risk management practices implemented by Airmax Internet L.L.C. to ensure proper handling of emergency service calls (911/E911) for VoIP services provided to customers.
              </p>
              <p className="text-gray-700">
                The purpose of this policy is to ensure that emergency calls are routed correctly, that emergency location information is properly maintained, and that customers are informed of the limitations of VoIP emergency services.
              </p>
            </div>
          </motion.div>

          {/* E911 Address Registration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">2. E911 ADDRESS REGISTRATION</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              All Direct Inward Dialing (DID) numbers provided to customers will have an associated registered physical service address configured through the VoIP service provider's E911 platform.
            </p>

            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Airmax Internet will ensure that:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Each DID number is assigned a valid physical address</li>
                <li>Emergency address information is maintained within the provider's E911 system</li>
                <li>Address records are updated if a customer changes service location</li>
                <li>Customers are responsible for notifying Airmax Internet if their service location changes</li>
              </ul>
            </div>
          </motion.div>

          {/* Emergency Call Routing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <PhoneIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">3. EMERGENCY CALL ROUTING</h2>
            </div>
            
            <p className="text-gray-700 mb-6">
              All 911 calls originating from Airmax VoIP services are routed directly to the upstream carrier's E911 network without modification or interception.
            </p>

            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Airmax Internet ensures that:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Emergency calls are never blocked or restricted</li>
                <li>Emergency calls are not routed through IVR systems or call queues</li>
                <li>Emergency calls are transmitted immediately to the E911 network</li>
              </ul>
            </div>
          </motion.div>

          {/* Customer Disclosure */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">4. CUSTOMER DISCLOSURE</h2>
            </div>
            
            <div className="bg-yellow-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 mb-4">
                Customers using Airmax VoIP services will be informed that VoIP emergency services have certain limitations compared to traditional landline services.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">These limitations include:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>911 service may not function during power outages</li>
                <li>911 service may not function if the customer's internet connection is unavailable</li>
                <li>If a device is moved to a different location, the registered emergency address may no longer be accurate unless updated</li>
                <li>Customers are advised to maintain an alternate method of emergency calling, such as a cellular phone</li>
              </ul>
            </div>
          </motion.div>

          {/* Location Change Policy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ArrowPathIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">5. LOCATION CHANGE POLICY</h2>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                If a customer relocates their VoIP equipment or service address, they must notify Airmax Internet immediately so the E911 service address can be updated.
              </p>
              <p className="text-gray-700 font-semibold">
                <strong>Warning:</strong> Failure to update location information may result in emergency responders being dispatched to an incorrect address.
              </p>
            </div>
          </motion.div>

          {/* System Testing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">6. SYSTEM TESTING</h2>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                Airmax Internet may periodically verify E911 configuration using the carrier's 933 emergency address verification service or other testing methods provided by the upstream carrier.
              </p>
              <p className="text-gray-700">
                This ensures that emergency address information is correctly registered in the E911 system.
              </p>
            </div>
          </motion.div>

          {/* Service Limitations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">7. SERVICE LIMITATIONS</h2>
            </div>
            
            <div className="bg-orange-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                VoIP emergency services depend on the availability of:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>Internet connectivity</li>
                <li>Electrical power</li>
                <li>The VoIP service provider's network infrastructure</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                Airmax Internet cannot guarantee that emergency calling will be available under all circumstances.
              </p>
            </div>
          </motion.div>

          {/* Responsibility */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">8. RESPONSIBILITY</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Airmax Internet Provides:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>VoIP platform</li>
                  <li>E911 configuration management</li>
                  <li>Upstream carrier coordination</li>
                </ul>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Customers Are Responsible For:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Providing accurate location information</li>
                  <li>Notifying Airmax of address changes</li>
                  <li>Maintaining backup emergency communication methods</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Policy Review */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <ArrowPathIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">9. POLICY REVIEW</h2>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700">
                This policy may be updated periodically to comply with applicable regulations and industry standards related to VoIP emergency services.
              </p>
            </div>
          </motion.div>

        </div>
      </section>
        </>
      )}

      {/* SMS Communications Policy */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-terms-block="true" className="bg-white rounded-2xl shadow-lg p-8 terms-block"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-airmax-blue rounded-full flex items-center justify-center mr-4">
                <PhoneIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">SMS COMMUNICATIONS POLICY</h2>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Consent to Receive SMS Messages</h3>
                <p className="text-gray-700 mb-4">
                  By voluntarily providing your mobile telephone number to AirMax Internet L.L.C., you expressly consent to receive text (SMS) messages related to your account, services, and customer support. Consent may be provided through our website, customer portal, service agreement, installation request, customer support interactions, or verbal authorization with an AirMax Internet representative.
                </p>
                <p className="text-gray-700">
                  Consent to receive SMS messages is not a condition of purchasing any AirMax Internet products or services.
                </p>
              </div>

              <div className="bg-green-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Types of SMS Messages</h3>
                <p className="text-gray-700 mb-4">
                  AirMax Internet may send SMS messages including, but not limited to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Installation scheduling and appointment reminders</li>
                  <li>Service activation notifications</li>
                  <li>Network outage notifications</li>
                  <li>Planned maintenance alerts</li>
                  <li>Billing reminders and payment confirmations</li>
                  <li>Customer support communications</li>
                  <li>Account notifications</li>
                  <li>Security verification messages</li>
                  <li>AI-assisted customer support responses</li>
                  <li>Responses to messages initiated by the customer</li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Frequency</h3>
                <p className="text-gray-700">
                  Message frequency varies depending on your account activity, service requests, support interactions, billing events, and network notifications.
                </p>
              </div>

              <div className="bg-orange-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Message and Data Rates</h3>
                <p className="text-gray-700">
                  Message and data rates may apply according to your mobile carrier's plan. AirMax Internet is not responsible for any charges imposed by your wireless carrier.
                </p>
              </div>

              <div className="bg-red-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Opt-Out</h3>
                <p className="text-gray-700 mb-4">
                  You may opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any AirMax Internet text message. After opting out, you will receive one final confirmation message, and no additional SMS messages will be sent unless you provide new consent or initiate a new conversation where messaging is required to fulfill your request.
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Help</h3>
                <p className="text-gray-700">
                  For assistance, reply <strong>HELP</strong> to any AirMax Internet SMS message or contact our customer support team at <strong>928-565-6707</strong>.
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy</h3>
                <p className="text-gray-700 mb-4">
                  AirMax Internet respects your privacy. Your mobile phone number and SMS consent information will only be used for providing services, customer support, account management, billing communications, appointment scheduling, and network notifications.
                </p>
                <p className="text-gray-700 font-semibold">
                  SMS consent is never sold, rented, shared, or disclosed to third parties or affiliates for their marketing or promotional purposes.
                </p>
                <p className="text-gray-700 mt-4">
                  For additional information regarding how we collect, use, and protect your personal information, please review our Privacy Policy.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Messaging</h3>
                <p className="text-gray-700">
                  AirMax Internet may utilize automated systems, including AI-assisted messaging, to provide customer support, appointment reminders, billing notifications, service updates, outage alerts, troubleshooting assistance, and other account-related communications. Customers may request assistance from a live AirMax Internet representative at any time.
                </p>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Changes</h3>
                <p className="text-gray-700">
                  AirMax Internet L.L.C. reserves the right to modify this SMS Communications Policy at any time. Continued use of our services after any modifications constitutes acceptance of the updated policy.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Questions About Our Terms?</h2>
            <p className="text-xl text-gray-600 mb-8">
              If you have any questions about these Terms and Conditions, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:928-565-6707" 
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Call 928-565-6707
              </a>
              <a 
                href="mailto:airmaxhelpdesk@gmail.com" 
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Email Us
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Phone Support: Mon–Fri 9 AM–5 PM (Arizona Time) · Online Support Center: Available 24/7
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
